import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography
} from "@material-ui/core";
import {
  AccessTime,
  EventAvailable,
  HeadsetMic,
  Message,
  TrendingUp,
  Warning
} from "@material-ui/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const formatNumber = value =>
  Number(value || 0).toLocaleString("pt-BR");

const formatCurrency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";

  return "Boa noite";
};

const getPeriodDates = period => {
  const end = new Date();
  const start = new Date();

  const daysByPeriod = {
    "1d": 0,
    "7d": 6,
    "30d": 29,
    "90d": 89
  };

  start.setDate(start.getDate() - (daysByPeriod[period] || 0));

  const formatDate = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  return {
    dateFrom: formatDate(start),
    dateTo: formatDate(end)
  };
};

const formatWaitingTime = value => {
  if (!value) return "Tempo não informado";

  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 60000)
  );

  if (minutes < 1) return "Agora";
  if (minutes < 60) return `Aguardando há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours < 24) {
    return remainingMinutes
      ? `Aguardando há ${hours}h ${remainingMinutes}min`
      : `Aguardando há ${hours}h`;
  }

  const days = Math.floor(hours / 24);
  return `Aguardando há ${days} dia${days > 1 ? "s" : ""}`;
};


const getHandoffCollectedItems = ticket => {
  const data =
    ticket && ticket.collectedData && typeof ticket.collectedData === "object"
      ? ticket.collectedData
      : {};

  return Object.entries(data)
    .filter(([key, value]) => {
      if (String(key).startsWith("__")) return false;
      if (value === null || value === undefined) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === "string" && !value.trim()) return false;
      return true;
    })
    .map(([key, value], index) => {
      let label = `Informação ${index + 1}`;

      if (key === "step_0") label = "Nome";
      else if (key === "step_1") label = "Interesse / motivo";
      else if (key === "step_2") label = "Qualificação";
      else if (/^step_\d+$/.test(key)) {
        label = `Informação coletada ${Number(key.split("_")[1]) + 1}`;
      } else {
        label = String(key)
          .replace(/_/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, char => char.toUpperCase());
      }

      const displayValue = Array.isArray(value)
        ? value.join(" • ")
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value);

      return {
        key,
        label,
        value: displayValue
      };
    });
};

const formatHandoffMessageTime = value => {
  if (!value) return "";

  try {
    return new Date(value).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch (_) {
    return "";
  }
};

const Dashboard = () => {
  const { user, socket } = useContext(AuthContext);
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState("7d");
  const [acceptingTicketId, setAcceptingTicketId] = useState(null);
  const [selectedHandoffTicket, setSelectedHandoffTicket] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get("/operations-dashboard", {
        params: getPeriodDates(period)
      });
      setData(response.data);
      setError(false);
    } catch (err) {
      console.error("[Dashboard] Erro ao carregar dados:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadDashboard();

    const interval = setInterval(loadDashboard, 30000);

    return () => clearInterval(interval);
  }, [loadDashboard]);

  // Quando a IA entrega uma conversa para uma pessoa,
  // atualizamos o Dashboard imediatamente, sem esperar o polling.
  useEffect(() => {
    if (!socket || !user?.companyId) return undefined;

    const eventName = `company-${user.companyId}-handRaise`;

    const handleHumanOpportunity = () => {
      loadDashboard();
    };

    socket.on(eventName, handleHumanOpportunity);

    return () => {
      socket.off(eventName, handleHumanOpportunity);
    };
  }, [socket, user?.companyId, loadDashboard]);

  const handleAssumeHandoff = useCallback(async ticket => {
    if (!ticket?.id || !user?.id || acceptingTicketId) return;

    try {
      setAcceptingTicketId(ticket.id);

      await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user.id
      });

      history.push(`/tickets/${ticket.uuid}`);
    } catch (err) {
      console.error("[Dashboard] Erro ao assumir atendimento:", err);
      window.alert("Não foi possível assumir este atendimento. Tente novamente.");
    } finally {
      setAcceptingTicketId(null);
    }
  }, [user?.id, history, acceptingTicketId]);

  const summary = data?.summary || {};
  const agendaToday = data?.agendaToday || [];
  const pipeline = data?.pipeline || [];
  const aiPendingTickets = data?.aiPendingTickets || [];
  const opportunities = data?.opportunities || {};

  const alerts = useMemo(() => {
    const items = [];

    if (Number(summary.pendingTickets || 0) > 0) {
      items.push({
        title: "Conversas aguardando atendimento",
        description: `${formatNumber(summary.pendingTickets)} conversa(s) aguardando a equipe.`
      });
    }

    if (Number(summary.staleManualTickets || 0) > 0) {
      items.push({
        title: "Atendimentos parados",
        description: `${formatNumber(summary.staleManualTickets)} atendimento(s) manual(is) estão sem movimentação.`
      });
    }

    if (Number(summary.appointmentsWithoutConfirmation || 0) > 0) {
      items.push({
        title: "Agendamentos sem confirmação",
        description: `${formatNumber(summary.appointmentsWithoutConfirmation)} agendamento(s) precisam de confirmação.`
      });
    }

    if (Number(summary.followupsPending || 0) > 0) {
      items.push({
        title: "Follow-ups pendentes",
        description: `${formatNumber(summary.followupsPending)} follow-up(s) ainda não foram enviados.`
      });
    }

    return items.slice(0, 4);
  }, [summary]);

  const cards = [
    {
      title: "Atendimentos Humanos",
      value: formatNumber(summary.openTickets),
      description: "Conversas em atendimento manual",
      icon: HeadsetMic,
      color: "#ef4444"
    },
    {
      title: "Vendas Iniciadas",
      value: formatNumber(summary.opportunitiesToday),
      description: "Oportunidades criadas hoje",
      icon: TrendingUp,
      color: "#22c55e"
    },
    {
      title: "Agendamentos",
      value: formatNumber(summary.appointmentsToday),
      description: "Agendamentos marcados para hoje",
      icon: EventAvailable,
      color: "#8b5cf6"
    },
    {
      title: "Respostas da IA",
      value: formatNumber(summary.aiResponsesToday),
      description: "Respostas realizadas hoje",
      icon: Message,
      color: "#2563eb"
    }
  ];

  const dailyMetrics = [
    {
      label: "Mensagens recebidas",
      value: summary.receivedToday
    },
    {
      label: "Mensagens enviadas",
      value: summary.sentToday
    },
    {
      label: "Follow-ups enviados",
      value: summary.followupsSentToday
    },
    {
      label: "Tickets criados",
      value: summary.ticketsToday
    }
  ];

  const commercialCards = [
    {
      title: "Receita",
      value: formatCurrency(opportunities.totalWon),
      description: "Valor total das vendas ganhas",
      accent: "#2563eb",
      background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)"
    },
    {
      title: "Vendas ganhas",
      value: formatNumber(opportunities.won?.count),
      description: "Negociações concluídas",
      accent: "#16a34a",
      background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)"
    },
    {
      title: "Conversão",
      value: `${Number(opportunities.conversion || 0).toLocaleString("pt-BR")}%`,
      description: "Ganhos entre negócios finalizados",
      accent: "#7c3aed",
      background: "linear-gradient(135deg, #ffffff 0%, #f5f3ff 100%)"
    },
    {
      title: "Ticket médio",
      value: formatCurrency(opportunities.ticketAverage),
      description: "Valor médio por venda",
      accent: "#ea580c",
      background: "linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)"
    }
  ];

  const operationsChartData = [
    {
      name: "Recebidas",
      value: Number(summary.receivedToday || 0)
    },
    {
      name: "Enviadas",
      value: Number(summary.sentToday || 0)
    },
    {
      name: "Follow-ups",
      value: Number(summary.followupsSentToday || 0)
    },
    {
      name: "Tickets",
      value: Number(summary.ticketsToday || 0)
    }
  ];

  const commercialChartData = [
    {
      name: "Receita",
      value: Number(opportunities.totalWon || 0)
    },
    {
      name: "Ticket médio",
      value: Number(opportunities.ticketAverage || 0)
    },
    {
      name: "Conversão",
      value: Number(opportunities.conversion || 0)
    },
    {
      name: "Vendas",
      value: Number(
        opportunities.won?.count || 0
      )
    }
  ];

  const serviceChartData = [
    {
      name: "IA",
      value: Number(
        summary.aiResponsesToday || 0
      ),
      color: "#2563EB"
    },
    {
      name: "Humanos",
      value: Number(
        summary.openTickets || 0
      ),
      color: "#EF4444"
    }
  ];

  const periodOptions = [
    { value: "1d", label: "Hoje" },
    { value: "7d", label: "7 dias" },
    { value: "30d", label: "30 dias" },
    { value: "90d", label: "90 dias" }
  ];

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  // ============================================================
  // KORAX PREMIUM HOME V2
  // Nova experiência principal.
  // A implementação anterior permanece preservada abaixo
  // para rollback seguro durante a validação.
  // ============================================================
  const premiumHomeEnabled = true;

  const premiumCurrency = value =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0
    }).format(Number(value || 0));

  const premiumPipelineTotal = pipeline.reduce(
    (total, item) => total + Number(item.count || 0),
    0
  );

  const premiumPipelineMax = Math.max(
    1,
    ...pipeline.map(item => Number(item.count || 0))
  );

  const premiumOccupancy = Math.max(
    0,
    Math.min(
      100,
      Number(
        data?.agendaOccupancy?.occupancyRate ??
        summary.occupancyRate ??
        0
      )
    )
  );

  const premiumAttention = (data?.radar || [])
    .filter(item => item.type !== "ok")
    .slice(0, 4);

  const premiumMetrics = [
    {
      icon: "💬",
      label: "Atendimentos hoje",
      value: formatNumber(summary.ticketsToday),
      detail: `${formatNumber(summary.messagesToday)} mensagens movimentadas`,
      accent: "#2563EB",
      soft: "#EFF6FF",
      route: "/tickets"
    },
    {
      icon: "📅",
      label: "Agendamentos hoje",
      value: formatNumber(summary.appointmentsToday),
      detail: `${formatNumber(summary.appointmentsWeek)} nesta semana`,
      accent: "#7C3AED",
      soft: "#F5F3FF",
      route: "/agenda"
    },
    {
      icon: "◎",
      label: "Oportunidades",
      value: formatNumber(opportunities.open?.count || summary.opportunitiesTotal),
      detail: `${premiumCurrency(opportunities.open?.total)} em pipeline`,
      accent: "#0891B2",
      soft: "#ECFEFF",
      route: "/crm"
    },
    {
      icon: "↗",
      label: "Conversão",
      value: `${Number(opportunities.conversion || 0)}%`,
      detail: `${formatNumber(opportunities.won?.count || 0)} conversões`,
      accent: "#16A34A",
      soft: "#F0FDF4",
      route: "/crm"
    }
  ];

  if (premiumHomeEnabled) {
    return (
      <Box
        p={3}
        style={{
          width: "100%",
          minHeight: "100%",
          boxSizing: "border-box",
          background:
            "linear-gradient(180deg, #F4F8FF 0px, #F8FAFC 360px, #F7F9FC 100%)"
        }}
      >
        <style>
          {`
            @keyframes kxPremiumFadeUp {
              from {
                opacity: 0;
                transform: translateY(12px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes kxPremiumPulse {
              0%, 100% {
                box-shadow: 0 0 0 0 rgba(74, 222, 128, .35);
              }
              50% {
                box-shadow: 0 0 0 7px rgba(74, 222, 128, 0);
              }
            }

            .kx-premium-enter {
              animation: kxPremiumFadeUp .5s ease both;
            }

            .kx-premium-card {
              transition:
                transform .2s ease,
                box-shadow .2s ease,
                border-color .2s ease;
            }

            .kx-premium-card:hover {
              transform: translateY(-4px);
              box-shadow: 0 18px 45px rgba(15,23,42,.09) !important;
              border-color: rgba(37,99,235,.18) !important;
            }

            .kx-premium-live-dot {
              animation: kxPremiumPulse 2s infinite;
            }

            .kx-premium-action {
              transition:
                transform .18s ease,
                background .18s ease,
                box-shadow .18s ease;
            }

            .kx-premium-action:hover {
              transform: translateY(-1px);
            }
          `}
        </style>

        {/* =====================================================
            TOP BAR / CONTEXTO
        ====================================================== */}
        <Box
          className="kx-premium-enter"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{
            gap: 14,
            marginBottom: 18
          }}
        >
          <Box>
            <Typography
              style={{
                color: "#0F172A",
                fontWeight: 950,
                fontSize: 27,
                letterSpacing: "-.04em",
                lineHeight: 1.1
              }}
            >
              Início
            </Typography>

            <Typography
              style={{
                color: "#64748B",
                fontSize: 13,
                marginTop: 6
              }}
            >
              Visão geral da sua operação em tempo real.
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{
              gap: 8,
              padding: 4,
              borderRadius: 13,
              border: "1px solid #E2E8F0",
              background: "#FFFFFF",
              boxShadow: "0 6px 20px rgba(15,23,42,.04)"
            }}
          >
            {periodOptions.map(option => (
              <Box
                key={option.value}
                onClick={() => setPeriod(option.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 9,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 800,
                  color:
                    period === option.value
                      ? "#FFFFFF"
                      : "#64748B",
                  background:
                    period === option.value
                      ? "#155EEF"
                      : "transparent",
                  boxShadow:
                    period === option.value
                      ? "0 5px 14px rgba(21,94,239,.20)"
                      : "none",
                  transition: "all .18s ease"
                }}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* =====================================================
            HERO
        ====================================================== */}
        <Paper
          elevation={0}
          className="kx-premium-enter"
          style={{
            position: "relative",
            overflow: "hidden",
            padding: "26px 28px",
            borderRadius: 22,
            border: "1px solid rgba(37,99,235,.16)",
            background:
              "linear-gradient(125deg, #071426 0%, #0D2D63 55%, #155EEF 100%)",
            boxShadow: "0 22px 55px rgba(15,43,94,.20)",
            marginBottom: 18
          }}
        >
          <Box
            style={{
              position: "absolute",
              width: 330,
              height: 330,
              borderRadius: "50%",
              right: -120,
              top: -185,
              background: "rgba(255,255,255,.08)",
              pointerEvents: "none"
            }}
          />

          <Box
            style={{
              position: "absolute",
              width: 190,
              height: 190,
              borderRadius: "50%",
              right: 190,
              bottom: -155,
              background: "rgba(96,165,250,.16)",
              pointerEvents: "none"
            }}
          />

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{
              position: "relative",
              zIndex: 1,
              gap: 20
            }}
          >
            <Box style={{ maxWidth: 720 }}>
              <Box
                display="flex"
                alignItems="center"
                style={{
                  gap: 9,
                  marginBottom: 13
                }}
              >
                <Box
                  className="kx-premium-live-dot"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#4ADE80"
                  }}
                />

                <Typography
                  style={{
                    color: "#BFDBFE",
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: ".11em",
                    textTransform: "uppercase"
                  }}
                >
                  Operação em tempo real
                </Typography>
              </Box>

              <Typography
                style={{
                  color: "#FFFFFF",
                  fontWeight: 950,
                  fontSize: "clamp(24px, 3vw, 34px)",
                  letterSpacing: "-.045em",
                  lineHeight: 1.08
                }}
              >
                {getGreeting()},{" "}
                {String(user?.name || "")
                  .trim()
                  .split(" ")[0] || "bem-vindo"}.
              </Typography>

              <Typography
                style={{
                  marginTop: 10,
                  color: "rgba(219,234,254,.82)",
                  fontSize: 13,
                  lineHeight: 1.7,
                  maxWidth: 620
                }}
              >
                Sua Korax movimentou{" "}
                <strong style={{ color: "#FFFFFF" }}>
                  {formatNumber(summary.messagesToday)}
                </strong>{" "}
                mensagens hoje, gerou{" "}
                <strong style={{ color: "#FFFFFF" }}>
                  {formatNumber(summary.opportunitiesToday)}
                </strong>{" "}
                oportunidade{Number(summary.opportunitiesToday) === 1 ? "" : "s"} e possui{" "}
                <strong style={{ color: "#FFFFFF" }}>
                  {formatNumber(summary.appointmentsToday)}
                </strong>{" "}
                agendamento{Number(summary.appointmentsToday) === 1 ? "" : "s"} para hoje.
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: 9 }}
            >
              <Box
                className="kx-premium-action"
                onClick={() => history.push("/tickets")}
                style={{
                  minHeight: 41,
                  padding: "0 15px",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 11,
                  border: "1px solid rgba(255,255,255,.20)",
                  background: "rgba(255,255,255,.11)",
                  color: "#FFFFFF",
                  fontSize: 12,
                  fontWeight: 850,
                  cursor: "pointer",
                  backdropFilter: "blur(10px)"
                }}
              >
                Abrir atendimentos
              </Box>

              <Box
                className="kx-premium-action"
                onClick={loadDashboard}
                style={{
                  minHeight: 41,
                  padding: "0 15px",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 11,
                  background: "#FFFFFF",
                  color: "#155EEF",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: loading ? "wait" : "pointer",
                  opacity: loading ? .7 : 1,
                  boxShadow: "0 7px 20px rgba(0,0,0,.12)"
                }}
              >
                {loading ? "Atualizando..." : "↻ Atualizar"}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* =====================================================
            OPORTUNIDADES AGUARDANDO ATENDIMENTO HUMANO
        ====================================================== */}
        {aiPendingTickets.length > 0 && (
          <Paper
            elevation={0}
            className="kx-premium-enter"
            style={{
              marginBottom: 18,
              padding: "22px 24px",
              borderRadius: 20,
              border: "1px solid rgba(249,115,22,.28)",
              background:
                "linear-gradient(135deg, #FFFFFF 0%, #FFF7ED 100%)",
              boxShadow: "0 16px 40px rgba(249,115,22,.10)"
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              style={{
                gap: 14,
                marginBottom: 18
              }}
            >
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  style={{ gap: 8 }}
                >
                  <Box
                    style={{
                      padding: "5px 9px",
                      borderRadius: 999,
                      background: "#FFEDD5",
                      color: "#C2410C",
                      fontSize: 9,
                      fontWeight: 950,
                      letterSpacing: ".08em",
                      textTransform: "uppercase"
                    }}
                  >
                    Ação necessária
                  </Box>

                  <Box
                    style={{
                      minWidth: 26,
                      height: 26,
                      padding: "0 8px",
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#EA580C",
                      color: "#FFFFFF",
                      fontSize: 11,
                      fontWeight: 950
                    }}
                  >
                    {aiPendingTickets.length}
                  </Box>
                </Box>

                <Typography
                  style={{
                    marginTop: 9,
                    color: "#0F172A",
                    fontSize: 19,
                    fontWeight: 950,
                    letterSpacing: "-.025em"
                  }}
                >
                  Oportunidades aguardando atendimento humano
                </Typography>

                <Typography
                  style={{
                    marginTop: 4,
                    color: "#64748B",
                    fontSize: 12,
                    lineHeight: 1.55
                  }}
                >
                  A Korax qualificou estes contatos e deixou o contexto pronto para sua equipe continuar.
                </Typography>
              </Box>

              <Box
                onClick={loadDashboard}
                style={{
                  padding: "8px 11px",
                  borderRadius: 10,
                  border: "1px solid #FED7AA",
                  background: "#FFFFFF",
                  color: "#C2410C",
                  fontSize: 11,
                  fontWeight: 850,
                  cursor: "pointer"
                }}
              >
                ↻ Atualizar
              </Box>
            </Box>

            <Grid container spacing={2}>
              {aiPendingTickets.slice(0, 6).map(ticket => {
                const summaryText =
                  ticket.handoffSummary ||
                  ticket.lastUserAnswer ||
                  "A IA concluiu a etapa automática e encaminhou esta conversa para atendimento humano.";

                const firstLetter = String(
                  ticket.contactName || "C"
                )
                  .trim()
                  .slice(0, 1)
                  .toUpperCase();

                const isAccepting =
                  acceptingTicketId === ticket.id;

                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    xl={4}
                    key={`handoff-${ticket.id}`}
                  >
                    <Box
                      onClick={() => setSelectedHandoffTicket(ticket)}
                      style={{
                        height: "100%",
                        boxSizing: "border-box",
                        padding: 17,
                        borderRadius: 16,
                        border: "1px solid #E7EAF0",
                        background: "#FFFFFF",
                        boxShadow:
                          "0 7px 22px rgba(15,23,42,.045)",
                        cursor: "pointer",
                        transition: "transform .16s ease, box-shadow .16s ease"
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ gap: 12 }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{
                            gap: 10,
                            minWidth: 0
                          }}
                        >
                          <Box
                            style={{
                              width: 38,
                              height: 38,
                              flexShrink: 0,
                              borderRadius: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background:
                                "linear-gradient(135deg, #EA580C, #FB923C)",
                              color: "#FFFFFF",
                              fontSize: 14,
                              fontWeight: 950
                            }}
                          >
                            {firstLetter}
                          </Box>

                          <Box style={{ minWidth: 0 }}>
                            <Typography
                              style={{
                                color: "#0F172A",
                                fontSize: 14,
                                fontWeight: 900,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}
                            >
                              {ticket.contactName}
                            </Typography>

                            <Typography
                              style={{
                                marginTop: 2,
                                color: "#EA580C",
                                fontSize: 10,
                                fontWeight: 800
                              }}
                            >
                              {formatWaitingTime(
                                ticket.handoffAt ||
                                ticket.updatedAt
                              )}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: "50%",
                            background: "#F97316",
                            boxShadow:
                              "0 0 0 5px rgba(249,115,22,.10)"
                          }}
                        />
                      </Box>

                      <Box
                        style={{
                          marginTop: 14,
                          padding: 13,
                          minHeight: 94,
                          borderRadius: 12,
                          background: "#F8FAFC",
                          border: "1px solid #EEF2F7"
                        }}
                      >
                        <Typography
                          style={{
                            color: "#64748B",
                            fontSize: 9,
                            fontWeight: 900,
                            letterSpacing: ".07em",
                            textTransform: "uppercase"
                          }}
                        >
                          Contexto coletado pela IA
                        </Typography>

                        <Typography
                          style={{
                            marginTop: 6,
                            color: "#334155",
                            fontSize: 11,
                            lineHeight: 1.6,
                            whiteSpace: "pre-line",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 5,
                            overflow: "hidden"
                          }}
                        >
                          {summaryText}
                        </Typography>
                      </Box>

                      <Box
                        onClick={event => {
                          event.stopPropagation();
                          handleAssumeHandoff(ticket);
                        }}
                        className="kx-premium-action"
                        style={{
                          marginTop: 13,
                          minHeight: 40,
                          width: "100%",
                          borderRadius: 11,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isAccepting
                            ? "#94A3B8"
                            : "#155EEF",
                          color: "#FFFFFF",
                          fontSize: 11,
                          fontWeight: 900,
                          cursor: isAccepting
                            ? "wait"
                            : "pointer",
                          boxShadow:
                            "0 7px 18px rgba(21,94,239,.18)",
                          userSelect: "none"
                        }}
                      >
                        {isAccepting
                          ? "Assumindo..."
                          : "Assumir atendimento →"}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}


        {selectedHandoffTicket && (
          <Box
            onClick={() => setSelectedHandoffTicket(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(15,23,42,.48)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 18
            }}
          >
            <Box
              onClick={event => event.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 720,
                maxHeight: "88vh",
                overflowY: "auto",
                background: "#FFFFFF",
                borderRadius: 22,
                boxShadow: "0 28px 80px rgba(15,23,42,.24)",
                border: "1px solid rgba(148,163,184,.22)"
              }}
            >
              <Box
                style={{
                  padding: "20px 22px",
                  borderBottom: "1px solid #EEF2F7",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 16
                }}
              >
                <Box style={{ minWidth: 0 }}>
                  <Typography
                    style={{
                      color: "#64748B",
                      fontSize: 9,
                      fontWeight: 950,
                      letterSpacing: ".09em",
                      textTransform: "uppercase"
                    }}
                  >
                    Oportunidade qualificada pela IA
                  </Typography>

                  <Typography
                    style={{
                      marginTop: 5,
                      color: "#0F172A",
                      fontSize: 21,
                      fontWeight: 950,
                      letterSpacing: "-.025em"
                    }}
                  >
                    {selectedHandoffTicket.contactName}
                  </Typography>

                  <Typography
                    style={{
                      marginTop: 4,
                      color: "#EA580C",
                      fontSize: 11,
                      fontWeight: 800
                    }}
                  >
                    {formatWaitingTime(
                      selectedHandoffTicket.handoffAt ||
                      selectedHandoffTicket.updatedAt
                    )}
                  </Typography>
                </Box>

                <Box
                  onClick={() => setSelectedHandoffTicket(null)}
                  style={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: 10,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: 18,
                    fontWeight: 700
                  }}
                >
                  ×
                </Box>
              </Box>

              <Box style={{ padding: 22 }}>
                <Box
                  style={{
                    padding: 16,
                    borderRadius: 14,
                    background:
                      "linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)",
                    border: "1px solid #FED7AA"
                  }}
                >
                  <Typography
                    style={{
                      color: "#C2410C",
                      fontSize: 9,
                      fontWeight: 950,
                      letterSpacing: ".08em",
                      textTransform: "uppercase"
                    }}
                  >
                    Resumo da IA
                  </Typography>

                  <Typography
                    style={{
                      marginTop: 7,
                      color: "#334155",
                      fontSize: 12,
                      lineHeight: 1.65,
                      whiteSpace: "pre-line"
                    }}
                  >
                    {selectedHandoffTicket.handoffSummary ||
                      selectedHandoffTicket.lastUserAnswer ||
                      "A IA concluiu a qualificação e encaminhou esta conversa para atendimento humano."}
                  </Typography>
                </Box>

                {getHandoffCollectedItems(selectedHandoffTicket).length > 0 && (
                  <Box style={{ marginTop: 20 }}>
                    <Typography
                      style={{
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 950
                      }}
                    >
                      Informações coletadas
                    </Typography>

                    <Grid container spacing={1} style={{ marginTop: 4 }}>
                      {getHandoffCollectedItems(selectedHandoffTicket).map(item => (
                        <Grid item xs={12} sm={6} key={item.key}>
                          <Box
                            style={{
                              height: "100%",
                              boxSizing: "border-box",
                              padding: "11px 12px",
                              borderRadius: 11,
                              border: "1px solid #E8EDF3",
                              background: "#F8FAFC"
                            }}
                          >
                            <Typography
                              style={{
                                color: "#64748B",
                                fontSize: 9,
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: ".055em"
                              }}
                            >
                              {item.label}
                            </Typography>

                            <Typography
                              style={{
                                marginTop: 5,
                                color: "#1E293B",
                                fontSize: 11,
                                lineHeight: 1.55,
                                fontWeight: 700,
                                whiteSpace: "pre-line"
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {Array.isArray(selectedHandoffTicket.recentMessages) &&
                  selectedHandoffTicket.recentMessages.length > 0 && (
                    <Box style={{ marginTop: 22 }}>
                      <Typography
                        style={{
                          color: "#0F172A",
                          fontSize: 13,
                          fontWeight: 950
                        }}
                      >
                        Conversa recente
                      </Typography>

                      <Box
                        style={{
                          marginTop: 10,
                          padding: 14,
                          borderRadius: 14,
                          background: "#F8FAFC",
                          border: "1px solid #E8EDF3"
                        }}
                      >
                        {selectedHandoffTicket.recentMessages.map(message => (
                          <Box
                            key={message.id}
                            style={{
                              marginBottom: 10,
                              display: "flex",
                              justifyContent: message.fromMe
                                ? "flex-start"
                                : "flex-end"
                            }}
                          >
                            <Box
                              style={{
                                maxWidth: "86%",
                                padding: "9px 11px",
                                borderRadius: 11,
                                background: message.fromMe
                                  ? "#FFFFFF"
                                  : "#EFF6FF",
                                border: message.fromMe
                                  ? "1px solid #E2E8F0"
                                  : "1px solid #DBEAFE"
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ gap: 10 }}
                              >
                                <Typography
                                  style={{
                                    color: message.fromMe
                                      ? "#64748B"
                                      : "#2563EB",
                                    fontSize: 8,
                                    fontWeight: 950,
                                    textTransform: "uppercase",
                                    letterSpacing: ".05em"
                                  }}
                                >
                                  {message.fromMe ? "IA" : "Cliente"}
                                </Typography>

                                <Typography
                                  style={{
                                    color: "#94A3B8",
                                    fontSize: 8,
                                    fontWeight: 700
                                  }}
                                >
                                  {formatHandoffMessageTime(message.createdAt)}
                                </Typography>
                              </Box>

                              <Typography
                                style={{
                                  marginTop: 4,
                                  color: "#334155",
                                  fontSize: 10.5,
                                  lineHeight: 1.55,
                                  whiteSpace: "pre-line"
                                }}
                              >
                                {message.body}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                <Box
                  display="flex"
                  style={{
                    marginTop: 22,
                    gap: 10,
                    flexWrap: "wrap"
                  }}
                >
                  <Box
                    onClick={() => setSelectedHandoffTicket(null)}
                    style={{
                      minHeight: 42,
                      padding: "0 16px",
                      borderRadius: 11,
                      border: "1px solid #CBD5E1",
                      background: "#FFFFFF",
                      color: "#475569",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Fechar
                  </Box>

                  <Box
                    onClick={() =>
                      handleAssumeHandoff(selectedHandoffTicket)
                    }
                    style={{
                      minHeight: 42,
                      flex: 1,
                      minWidth: 190,
                      padding: "0 18px",
                      borderRadius: 11,
                      background:
                        acceptingTicketId === selectedHandoffTicket.id
                          ? "#94A3B8"
                          : "#155EEF",
                      color: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 950,
                      cursor:
                        acceptingTicketId === selectedHandoffTicket.id
                          ? "wait"
                          : "pointer",
                      boxShadow: "0 8px 20px rgba(21,94,239,.20)"
                    }}
                  >
                    {acceptingTicketId === selectedHandoffTicket.id
                      ? "Assumindo..."
                      : "Assumir atendimento →"}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* =====================================================
            KPIs
        ====================================================== */}
        <Grid
          container
          spacing={2}
          className="kx-premium-enter"
        >
          {premiumMetrics.map((metric, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              lg={3}
              key={metric.label}
            >
              <Paper
                elevation={0}
                className="kx-premium-card"
                onClick={() => history.push(metric.route)}
                style={{
                  minHeight: 150,
                  height: "100%",
                  padding: 19,
                  boxSizing: "border-box",
                  borderRadius: 18,
                  border: "1px solid #E5EAF1",
                  background: "#FFFFFF",
                  boxShadow: "0 9px 28px rgba(15,23,42,.045)",
                  cursor: "pointer",
                  animationDelay: `${index * 70}ms`
                }}
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      style={{
                        color: "#64748B",
                        fontSize: 11,
                        fontWeight: 850,
                        textTransform: "uppercase",
                        letterSpacing: ".045em"
                      }}
                    >
                      {metric.label}
                    </Typography>

                    <Typography
                      style={{
                        marginTop: 10,
                        color: "#0F172A",
                        fontSize: 29,
                        lineHeight: 1,
                        fontWeight: 950,
                        letterSpacing: "-.04em"
                      }}
                    >
                      {loading && !data ? "..." : metric.value}
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: metric.soft,
                      color: metric.accent,
                      fontSize: 19,
                      fontWeight: 900
                    }}
                  >
                    {metric.icon}
                  </Box>
                </Box>

                <Typography
                  style={{
                    marginTop: 17,
                    color: "#94A3B8",
                    fontSize: 11.5,
                    fontWeight: 650
                  }}
                >
                  {metric.detail}
                </Typography>

                <Box
                  style={{
                    width: 34,
                    height: 3,
                    marginTop: 12,
                    borderRadius: 99,
                    background: metric.accent
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* =====================================================
            INTELIGÊNCIA DA OPERAÇÃO
        ====================================================== */}
        <Box
          className="kx-premium-enter"
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{
            marginTop: 25,
            marginBottom: 11,
            gap: 10
          }}
        >
          <Box>
            <Typography
              style={{
                color: "#0F172A",
                fontWeight: 950,
                fontSize: 18,
                letterSpacing: "-.025em"
              }}
            >
              Inteligência da operação
            </Typography>

            <Typography
              style={{
                color: "#94A3B8",
                fontSize: 12,
                marginTop: 4
              }}
            >
              O que está acontecendo agora na sua empresa.
            </Typography>
          </Box>

          <Typography
            style={{
              color: "#94A3B8",
              fontSize: 11
            }}
          >
            Dados atualizados automaticamente
          </Typography>
        </Box>

        <Grid
          container
          spacing={2}
          className="kx-premium-enter"
        >
          {/* GRÁFICO OPERACIONAL */}
          <Grid item xs={12} lg={8}>
            <Paper
              elevation={0}
              className="kx-premium-card"
              style={{
                minHeight: 355,
                height: "100%",
                padding: 21,
                boxSizing: "border-box",
                borderRadius: 18,
                border: "1px solid #E5EAF1",
                background: "#FFFFFF",
                boxShadow: "0 10px 30px rgba(15,23,42,.045)"
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ marginBottom: 18 }}
              >
                <Box>
                  <Typography
                    style={{
                      color: "#0F172A",
                      fontSize: 14,
                      fontWeight: 900
                    }}
                  >
                    Atividade de hoje
                  </Typography>

                  <Typography
                    style={{
                      color: "#94A3B8",
                      fontSize: 11,
                      marginTop: 4
                    }}
                  >
                    Volume da operação e automações executadas.
                  </Typography>
                </Box>

                <Box
                  style={{
                    padding: "6px 9px",
                    borderRadius: 9,
                    background: "#EFF6FF",
                    color: "#2563EB",
                    fontSize: 10,
                    fontWeight: 850
                  }}
                >
                  AO VIVO
                </Box>
              </Box>

              <Box style={{ width: "100%", height: 270 }}>
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <BarChart
                    data={operationsChartData}
                    margin={{
                      top: 8,
                      right: 8,
                      left: -15,
                      bottom: 0
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="rgba(148,163,184,.18)"
                    />

                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: "#64748B",
                        fontSize: 10,
                        fontWeight: 700
                      }}
                    />

                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      tick={{
                        fill: "#94A3B8",
                        fontSize: 9
                      }}
                    />

                    <Tooltip
                      cursor={{
                        fill: "rgba(37,99,235,.045)"
                      }}
                      formatter={value => [
                        formatNumber(value),
                        "Quantidade"
                      ]}
                    />

                    <Bar
                      dataKey="value"
                      fill="#2563EB"
                      radius={[7, 7, 2, 2]}
                      animationDuration={900}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* DONUT AGENDA */}
          <Grid item xs={12} lg={4}>
            <Paper
              elevation={0}
              className="kx-premium-card"
              onClick={() => history.push("/agenda")}
              style={{
                minHeight: 355,
                height: "100%",
                padding: 21,
                boxSizing: "border-box",
                borderRadius: 18,
                border: "1px solid #E5EAF1",
                background: "#FFFFFF",
                boxShadow: "0 10px 30px rgba(15,23,42,.045)",
                cursor: "pointer"
              }}
            >
              <Typography
                style={{
                  color: "#0F172A",
                  fontSize: 14,
                  fontWeight: 900
                }}
              >
                Ocupação da agenda
              </Typography>

              <Typography
                style={{
                  color: "#94A3B8",
                  fontSize: 11,
                  marginTop: 4
                }}
              >
                Capacidade utilizada hoje.
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  paddingTop: 27,
                  paddingBottom: 18
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    width: 158,
                    height: 158,
                    borderRadius: "50%",
                    background:
                      `conic-gradient(#2563EB 0 ${premiumOccupancy}%, #E8EEF7 ${premiumOccupancy}% 100%)`,
                    position: "relative",
                    boxShadow:
                      "0 10px 28px rgba(37,99,235,.10)"
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    style={{
                      width: 116,
                      height: 116,
                      borderRadius: "50%",
                      background: "#FFFFFF"
                    }}
                  >
                    <Typography
                      style={{
                        color: "#0F172A",
                        fontSize: 29,
                        fontWeight: 950,
                        letterSpacing: "-.04em"
                      }}
                    >
                      {premiumOccupancy}%
                    </Typography>

                    <Typography
                      style={{
                        color: "#94A3B8",
                        fontSize: 10,
                        fontWeight: 750
                      }}
                    >
                      ocupada
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  padding: "11px 13px",
                  background: "#F8FAFC",
                  borderRadius: 11
                }}
              >
                <Typography
                  style={{
                    color: "#64748B",
                    fontSize: 11,
                    fontWeight: 700
                  }}
                >
                  Agendamentos hoje
                </Typography>

                <Typography
                  style={{
                    color: "#155EEF",
                    fontSize: 13,
                    fontWeight: 950
                  }}
                >
                  {formatNumber(summary.appointmentsToday)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* =====================================================
            CRM + AGENDA
        ====================================================== */}
        <Grid
          container
          spacing={2}
          className="kx-premium-enter"
          style={{ marginTop: 2 }}
        >
          {/* CRM */}
          <Grid item xs={12} lg={7}>
            <Paper
              elevation={0}
              className="kx-premium-card"
              style={{
                minHeight: 350,
                height: "100%",
                padding: 21,
                boxSizing: "border-box",
                borderRadius: 18,
                border: "1px solid #E5EAF1",
                background: "#FFFFFF",
                boxShadow: "0 10px 30px rgba(15,23,42,.045)"
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ marginBottom: 20 }}
              >
                <Box>
                  <Typography
                    style={{
                      color: "#0F172A",
                      fontSize: 14,
                      fontWeight: 900
                    }}
                  >
                    Funil do CRM
                  </Typography>

                  <Typography
                    style={{
                      color: "#94A3B8",
                      fontSize: 11,
                      marginTop: 4
                    }}
                  >
                    {premiumPipelineTotal} oportunidade{premiumPipelineTotal === 1 ? "" : "s"} distribuída{premiumPipelineTotal === 1 ? "" : "s"} no pipeline.
                  </Typography>
                </Box>

                <Box
                  className="kx-premium-action"
                  onClick={() => history.push("/crm")}
                  style={{
                    padding: "8px 11px",
                    borderRadius: 9,
                    background: "#EFF6FF",
                    color: "#155EEF",
                    fontSize: 10.5,
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Abrir CRM →
                </Box>
              </Box>

              {pipeline.length === 0 ? (
                <Box
                  style={{
                    padding: 24,
                    borderRadius: 13,
                    border: "1px dashed #CBD5E1",
                    background: "#F8FAFC",
                    color: "#94A3B8",
                    fontSize: 12,
                    textAlign: "center"
                  }}
                >
                  Seu pipeline aparecerá aqui assim que o CRM estiver configurado.
                </Box>
              ) : (
                pipeline.map(item => {
                  const count = Number(item.count || 0);
                  const width = Math.max(
                    count > 0 ? 8 : 0,
                    (count / premiumPipelineMax) * 100
                  );

                  return (
                    <Box
                      key={item.id || item.name}
                      style={{
                        marginBottom: 15
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ marginBottom: 6 }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 8 }}
                        >
                          <Box
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: item.color || "#2563EB"
                            }}
                          />

                          <Typography
                            style={{
                              color: "#334155",
                              fontSize: 11.5,
                              fontWeight: 780
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 10 }}
                        >
                          {Number(item.value || 0) > 0 && (
                            <Typography
                              style={{
                                color: "#94A3B8",
                                fontSize: 10
                              }}
                            >
                              {premiumCurrency(item.value)}
                            </Typography>
                          )}

                          <Typography
                            style={{
                              color: "#0F172A",
                              fontSize: 11,
                              fontWeight: 950
                            }}
                          >
                            {count}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        style={{
                          width: "100%",
                          height: 7,
                          borderRadius: 99,
                          background: "#EEF2F7",
                          overflow: "hidden"
                        }}
                      >
                        <Box
                          style={{
                            width: `${width}%`,
                            height: "100%",
                            borderRadius: 99,
                            background: item.color || "#2563EB",
                            transition: "width .6s ease"
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })
              )}
            </Paper>
          </Grid>

          {/* AGENDA */}
          <Grid item xs={12} lg={5}>
            <Paper
              elevation={0}
              className="kx-premium-card"
              style={{
                minHeight: 350,
                height: "100%",
                padding: 21,
                boxSizing: "border-box",
                borderRadius: 18,
                border: "1px solid #E5EAF1",
                background: "#FFFFFF",
                boxShadow: "0 10px 30px rgba(15,23,42,.045)"
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ marginBottom: 17 }}
              >
                <Box>
                  <Typography
                    style={{
                      color: "#0F172A",
                      fontSize: 14,
                      fontWeight: 900
                    }}
                  >
                    Agenda de hoje
                  </Typography>

                  <Typography
                    style={{
                      color: "#94A3B8",
                      fontSize: 11,
                      marginTop: 4
                    }}
                  >
                    Próximos compromissos.
                  </Typography>
                </Box>

                <Box
                  className="kx-premium-action"
                  onClick={() => history.push("/agenda")}
                  style={{
                    padding: "8px 11px",
                    borderRadius: 9,
                    color: "#7C3AED",
                    background: "#F5F3FF",
                    fontSize: 10.5,
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Ver agenda →
                </Box>
              </Box>

              {agendaToday.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  style={{
                    height: 220,
                    textAlign: "center"
                  }}
                >
                  <Box
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 13,
                      background: "#F5F3FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20
                    }}
                  >
                    📅
                  </Box>

                  <Typography
                    style={{
                      marginTop: 12,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 800
                    }}
                  >
                    Agenda livre por enquanto
                  </Typography>

                  <Typography
                    style={{
                      marginTop: 4,
                      color: "#94A3B8",
                      fontSize: 10.5
                    }}
                  >
                    Novos agendamentos aparecerão aqui.
                  </Typography>
                </Box>
              ) : (
                agendaToday.slice(0, 5).map((item, index) => (
                  <Box
                    key={item.id}
                    display="flex"
                    alignItems="center"
                    style={{
                      padding: "12px 0",
                      borderBottom:
                        index === Math.min(agendaToday.length, 5) - 1
                          ? "none"
                          : "1px solid #EEF2F7"
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        width: 44,
                        minWidth: 44,
                        height: 44,
                        marginRight: 11,
                        borderRadius: 12,
                        background: "#F5F3FF"
                      }}
                    >
                      <Typography
                        style={{
                          color: "#7C3AED",
                          fontSize: 11,
                          fontWeight: 950
                        }}
                      >
                        {item.startTime
                          ? new Date(item.startTime)
                              .toLocaleTimeString("pt-BR", {
                                hour: "2-digit",
                                minute: "2-digit"
                              })
                          : "--:--"}
                      </Typography>
                    </Box>

                    <Box
                      style={{
                        minWidth: 0,
                        flex: 1
                      }}
                    >
                      <Typography
                        style={{
                          color: "#0F172A",
                          fontSize: 11.5,
                          fontWeight: 850,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {item.contactName}
                      </Typography>

                      <Typography
                        style={{
                          color: "#94A3B8",
                          fontSize: 10,
                          marginTop: 3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {item.serviceName}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* =====================================================
            ATENÇÃO
        ====================================================== */}
        <Paper
          elevation={0}
          className="kx-premium-card kx-premium-enter"
          style={{
            marginTop: 16,
            padding: 20,
            borderRadius: 18,
            border: "1px solid #E5EAF1",
            background: "#FFFFFF",
            boxShadow: "0 10px 30px rgba(15,23,42,.045)"
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{
              gap: 12,
              marginBottom: premiumAttention.length ? 15 : 0
            }}
          >
            <Box>
              <Typography
                style={{
                  color: "#0F172A",
                  fontSize: 14,
                  fontWeight: 900
                }}
              >
                Precisa da sua atenção
              </Typography>

              <Typography
                style={{
                  color: "#94A3B8",
                  fontSize: 11,
                  marginTop: 4
                }}
              >
                A Korax destaca situações que merecem acompanhamento.
              </Typography>
            </Box>

            {premiumAttention.length === 0 && (
              <Box
                display="flex"
                alignItems="center"
                style={{
                  gap: 7,
                  padding: "7px 10px",
                  borderRadius: 9,
                  background: "#F0FDF4",
                  color: "#15803D",
                  fontSize: 10.5,
                  fontWeight: 850
                }}
              >
                <span>●</span>
                Tudo sob controle
              </Box>
            )}
          </Box>

          {premiumAttention.length > 0 && (
            <Grid container spacing={1}>
              {premiumAttention.map((item, index) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={`${item.title}-${index}`}
                >
                  <Box
                    onClick={() =>
                      item.route && history.push(item.route)
                    }
                    style={{
                      padding: "13px 14px",
                      borderRadius: 12,
                      cursor: item.route ? "pointer" : "default",
                      background:
                        item.type === "danger"
                          ? "#FEF2F2"
                          : item.type === "warning"
                          ? "#FFF7ED"
                          : "#EFF6FF",
                      border:
                        item.type === "danger"
                          ? "1px solid #FECACA"
                          : item.type === "warning"
                          ? "1px solid #FED7AA"
                          : "1px solid #DBEAFE"
                    }}
                  >
                    <Typography
                      style={{
                        color:
                          item.type === "danger"
                            ? "#B91C1C"
                            : item.type === "warning"
                            ? "#C2410C"
                            : "#1D4ED8",
                        fontSize: 11,
                        fontWeight: 850
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            padding: "22px 0 8px"
          }}
        >
          <Typography
            style={{
              color: "#CBD5E1",
              fontSize: 9.5,
              fontWeight: 750,
              letterSpacing: ".06em",
              textTransform: "uppercase"
            }}
          >
            Korax · Inteligência aplicada à sua operação
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      p={3}
      style={{
        width: "100%",
        minHeight: "100%",
        boxSizing: "border-box",
        background: "linear-gradient(180deg, #f3f7ff 0%, #f8fafc 280px, #f7f9fc 100%)"
      }}
    >
      <Box
        style={{
          position: "relative",
          overflow: "hidden",
          marginBottom: 24,
          padding: "30px 32px",
          borderRadius: 24,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #071329 0%, #0B2C68 52%, #155EEF 100%)",
          boxShadow:
            "0 24px 54px rgba(15, 43, 94, .24)"
        }}
      >
        <Box
          style={{
            position: "absolute",
            width: 340,
            height: 340,
            borderRadius: "50%",
            right: -110,
            top: -190,
            background: "rgba(255,255,255,.09)",
            pointerEvents: "none"
          }}
        />

        <Box
          style={{
            position: "absolute",
            width: 230,
            height: 230,
            borderRadius: "50%",
            right: 150,
            bottom: -185,
            background: "rgba(96,165,250,.18)",
            pointerEvents: "none"
          }}
        />

        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{
            position: "relative",
            zIndex: 1,
            gap: 22
          }}
        >
          <Box style={{ maxWidth: 690 }}>
            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 9,
                marginBottom: 12
              }}
            >
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#4ADE80",
                  boxShadow:
                    "0 0 0 5px rgba(74,222,128,.14)"
                }}
              />

              <Typography
                style={{
                  color: "#BFDBFE",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: ".12em",
                  textTransform: "uppercase"
                }}
              >
                Operação em tempo real
              </Typography>
            </Box>

            <Typography
              style={{
                color: "#ffffff",
                fontSize: "clamp(26px, 3vw, 38px)",
                lineHeight: 1.08,
                fontWeight: 900,
                letterSpacing: "-.045em"
              }}
            >
              Centro de Operações Korax
            </Typography>

            <Typography
              style={{
                marginTop: 10,
                color: "rgba(219,234,254,.86)",
                fontSize: 15,
                fontWeight: 600
              }}
            >
              {getGreeting()},{" "}
              {String(user?.name || "")
                .trim()
                .split(" ")[0] || "seja bem-vindo"}.
            </Typography>

            <Typography
              style={{
                marginTop: 8,
                color: "rgba(219,234,254,.76)",
                fontSize: 13,
                lineHeight: 1.65
              }}
            >
              Hoje sua IA respondeu{" "}
              <strong style={{ color: "#ffffff" }}>
                {formatNumber(summary.aiResponsesToday)}
              </strong>{" "}
              mensagens, existem{" "}
              <strong style={{ color: "#ffffff" }}>
                {formatNumber(summary.opportunitiesToday)}
              </strong>{" "}
              novas oportunidades e{" "}
              <strong style={{ color: "#ffffff" }}>
                {formatNumber(summary.followupsPending)}
              </strong>{" "}
              follow-ups aguardando atenção.
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 10 }}
          >
            <Box
              onClick={() => history.push("/tickets")}
              style={{
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                padding: "0 15px",
                borderRadius: 11,
                border: "1px solid rgba(255,255,255,.22)",
                background: "rgba(255,255,255,.12)",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
                backdropFilter: "blur(8px)"
              }}
            >
              Abrir conversas
            </Box>

            <Box
              onClick={loadDashboard}
              style={{
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                padding: "0 15px",
                borderRadius: 11,
                border: "1px solid rgba(255,255,255,.22)",
                background: "rgba(255,255,255,.12)",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 800,
                cursor: loading
                  ? "not-allowed"
                  : "pointer",
                opacity: loading ? 0.65 : 1,
                backdropFilter: "blur(8px)"
              }}
            >
              {loading
                ? "Atualizando..."
                : "↻ Atualizar dados"}
            </Box>
          </Box>
        </Box>

        <Grid
          container
          spacing={2}
          style={{
            position: "relative",
            zIndex: 1,
            marginTop: 18
          }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Box
              style={{
                height: "100%",
                padding: "15px 16px",
                borderRadius: 15,
                background: "rgba(255,255,255,.10)",
                border: "1px solid rgba(255,255,255,.14)",
                backdropFilter: "blur(8px)"
              }}
            >
              <Typography
                style={{
                  color: "rgba(219,234,254,.72)",
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".06em"
                }}
              >
                Atendimentos humanos
              </Typography>

              <Typography
                style={{
                  marginTop: 7,
                  color: "#ffffff",
                  fontSize: 22,
                  fontWeight: 900
                }}
              >
                {formatNumber(summary.openTickets)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box
              style={{
                height: "100%",
                padding: "15px 16px",
                borderRadius: 15,
                background: "rgba(255,255,255,.10)",
                border: "1px solid rgba(255,255,255,.14)",
                backdropFilter: "blur(8px)"
              }}
            >
              <Typography
                style={{
                  color: "rgba(219,234,254,.72)",
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".06em"
                }}
              >
                Oportunidades hoje
              </Typography>

              <Typography
                style={{
                  marginTop: 7,
                  color: "#ffffff",
                  fontSize: 22,
                  fontWeight: 900
                }}
              >
                {formatNumber(
                  summary.opportunitiesToday
                )}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box
              style={{
                height: "100%",
                padding: "15px 16px",
                borderRadius: 15,
                background: "rgba(255,255,255,.10)",
                border: "1px solid rgba(255,255,255,.14)",
                backdropFilter: "blur(8px)"
              }}
            >
              <Typography
                style={{
                  color: "rgba(219,234,254,.72)",
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".06em"
                }}
              >
                Respostas da IA
              </Typography>

              <Typography
                style={{
                  marginTop: 7,
                  color: "#ffffff",
                  fontSize: 22,
                  fontWeight: 900
                }}
              >
                {formatNumber(
                  summary.aiResponsesToday
                )}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box
              style={{
                height: "100%",
                padding: "15px 16px",
                borderRadius: 15,
                background: "rgba(255,255,255,.10)",
                border: "1px solid rgba(255,255,255,.14)",
                backdropFilter: "blur(8px)"
              }}
            >
              <Typography
                style={{
                  color: "rgba(219,234,254,.72)",
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".06em"
                }}
              >
                Agendamentos hoje
              </Typography>

              <Typography
                style={{
                  marginTop: 7,
                  color: "#ffffff",
                  fontSize: 22,
                  fontWeight: 900
                }}
              >
                {formatNumber(
                  summary.appointmentsToday
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Paper
          elevation={0}
          style={{
            padding: 16,
            marginBottom: 24,
            borderRadius: 14,
            border: "1px solid rgba(239,68,68,.22)",
            background: "rgba(239,68,68,.05)"
          }}
        >
          <Typography
            variant="body2"
            style={{
              color: "#dc2626",
              fontWeight: 600
            }}
          >
            Não foi possível atualizar os dados neste momento.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        {cards.map(card => {
          const Icon = card.icon;

          return (
            <Grid item xs={12} sm={6} lg={3} key={card.title}>
              <Paper
                elevation={0}
                style={{
                  height: "100%",
                  minHeight: 166,
                  borderRadius: 16,
                  padding: 22,
                  boxSizing: "border-box",
                  border: `1px solid ${card.color}20`,
                  background: `linear-gradient(135deg, #ffffff 0%, ${card.color}0D 100%)`,
                  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)"
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 13,
                      background: `${card.color}14`
                    }}
                  >
                    <Icon
                      style={{
                        fontSize: 26,
                        color: card.color
                      }}
                    />
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  style={{
                    color: "#64748b",
                    marginTop: 17,
                    fontWeight: 600
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  variant="h4"
                  style={{
                    fontWeight: 800,
                    color: "#0f172a",
                    marginTop: 4
                  }}
                >
                  {loading && !data ? "..." : card.value}
                </Typography>

                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    color: "#94a3b8",
                    marginTop: 7
                  }}
                >
                  {card.description}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box
        style={{
          marginTop: 22,
          marginBottom: 4
        }}
      >
        <Typography
          style={{
            color: "#0f172a",
            fontSize: 18,
            fontWeight: 900,
            letterSpacing: "-.02em"
          }}
        >
          Inteligência da operação
        </Typography>

        <Typography
          variant="body2"
          style={{
            marginTop: 4,
            color: "#94a3b8"
          }}
        >
          Indicadores visuais da atividade comercial e do atendimento.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            style={{
              height: "100%",
              minHeight: 355,
              padding: 22,
              boxSizing: "border-box",
              borderRadius: 18,
              border: "1px solid #e7edf4",
              background: "#ffffff",
              boxShadow:
                "0 14px 34px rgba(15,23,42,.05)"
            }}
          >
            <Typography
              style={{
                color: "#0f172a",
                fontSize: 15,
                fontWeight: 900
              }}
            >
              Operação do dia
            </Typography>

            <Typography
              variant="body2"
              style={{
                marginTop: 4,
                marginBottom: 18,
                color: "#94a3b8"
              }}
            >
              Mensagens, tickets e follow-ups movimentados hoje.
            </Typography>

            <Box style={{ width: "100%", height: 255 }}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={operationsChartData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: -12,
                    bottom: 0
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgba(148,163,184,.20)"
                  />

                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "#64748b",
                      fontSize: 11,
                      fontWeight: 700
                    }}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 10
                    }}
                  />

                  <Tooltip
                    cursor={{
                      fill: "rgba(37,99,235,.05)"
                    }}
                    formatter={value => [
                      formatNumber(value),
                      "Quantidade"
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border:
                        "1px solid rgba(148,163,184,.22)",
                      boxShadow:
                        "0 12px 30px rgba(15,23,42,.12)",
                      fontFamily:
                        "'Inter', system-ui, sans-serif",
                      fontSize: 12
                    }}
                  />

                  <Bar
                    dataKey="value"
                    radius={[8, 8, 3, 3]}
                    maxBarSize={58}
                  >
                    {operationsChartData.map(
                      (entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={
                            [
                              "#2563EB",
                              "#0EA5E9",
                              "#8B5CF6",
                              "#22C55E"
                            ][index]
                          }
                        />
                      )
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            style={{
              height: "100%",
              minHeight: 355,
              padding: 22,
              boxSizing: "border-box",
              borderRadius: 18,
              border: "1px solid #e7edf4",
              background: "#ffffff",
              boxShadow:
                "0 14px 34px rgba(15,23,42,.05)"
            }}
          >
            <Typography
              style={{
                color: "#0f172a",
                fontSize: 15,
                fontWeight: 900
              }}
            >
              IA x atendimento humano
            </Typography>

            <Typography
              variant="body2"
              style={{
                marginTop: 4,
                color: "#94a3b8"
              }}
            >
              Participação da IA e da equipe na operação atual.
            </Typography>

            <Box style={{ width: "100%", height: 225 }}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <PieChart>
                  <Pie
                    data={serviceChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="52%"
                    innerRadius={62}
                    outerRadius={91}
                    paddingAngle={4}
                    stroke="none"
                  >
                    {serviceChartData.map(item => (
                      <Cell
                        key={item.name}
                        fill={item.color}
                      />
                    ))}
                  </Pie>

                  <Tooltip
                    formatter={(value, name) => [
                      formatNumber(value),
                      name
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border:
                        "1px solid rgba(148,163,184,.22)",
                      boxShadow:
                        "0 12px 30px rgba(15,23,42,.12)",
                      fontFamily:
                        "'Inter', system-ui, sans-serif",
                      fontSize: 12
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              flexWrap="wrap"
              style={{ gap: 16 }}
            >
              {serviceChartData.map(item => (
                <Box
                  key={item.name}
                  display="flex"
                  alignItems="center"
                  style={{
                    gap: 7,
                    color: "#475569",
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  <Box
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      background: item.color
                    }}
                  />

                  {item.name}:{" "}
                  {formatNumber(item.value)}
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            style={{
              minHeight: 340,
              padding: 22,
              boxSizing: "border-box",
              borderRadius: 18,
              border: "1px solid #e7edf4",
              background: "#ffffff",
              boxShadow:
                "0 14px 34px rgba(15,23,42,.05)"
            }}
          >
            <Typography
              style={{
                color: "#0f172a",
                fontSize: 15,
                fontWeight: 900
              }}
            >
              Desempenho comercial
            </Typography>

            <Typography
              variant="body2"
              style={{
                marginTop: 4,
                marginBottom: 18,
                color: "#94a3b8"
              }}
            >
              Receita, ticket médio, conversão e vendas no período selecionado.
            </Typography>

            <Box style={{ width: "100%", height: 245 }}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={commercialChartData}
                  layout="vertical"
                  margin={{
                    top: 4,
                    right: 24,
                    left: 18,
                    bottom: 0
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke="rgba(148,163,184,.20)"
                  />

                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 10
                    }}
                    tickFormatter={value =>
                      Number(value || 0).toLocaleString(
                        "pt-BR",
                        {
                          notation: "compact",
                          maximumFractionDigits: 1
                        }
                      )
                    }
                  />

                  <YAxis
                    type="category"
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    width={92}
                    tick={{
                      fill: "#475569",
                      fontSize: 11,
                      fontWeight: 700
                    }}
                  />

                  <Tooltip
                    cursor={{
                      fill: "rgba(37,99,235,.04)"
                    }}
                    formatter={(value, _name, props) => {
                      const label =
                        props?.payload?.name || "";

                      if (
                        label === "Receita" ||
                        label === "Ticket médio"
                      ) {
                        return [
                          formatCurrency(value),
                          label
                        ];
                      }

                      if (label === "Conversão") {
                        return [
                          `${Number(value || 0).toLocaleString(
                            "pt-BR"
                          )}%`,
                          label
                        ];
                      }

                      return [
                        formatNumber(value),
                        label
                      ];
                    }}
                    contentStyle={{
                      borderRadius: 12,
                      border:
                        "1px solid rgba(148,163,184,.22)",
                      boxShadow:
                        "0 12px 30px rgba(15,23,42,.12)",
                      fontFamily:
                        "'Inter', system-ui, sans-serif",
                      fontSize: 12
                    }}
                  />

                  <Bar
                    dataKey="value"
                    radius={[0, 8, 8, 0]}
                    maxBarSize={34}
                  >
                    {commercialChartData.map(
                      (entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={
                            [
                              "#2563EB",
                              "#F97316",
                              "#8B5CF6",
                              "#22C55E"
                            ][index]
                          }
                        />
                      )
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        style={{
          marginTop: 22,
          padding: 22,
          borderRadius: 18,
          border: "1px solid #dbe7f5",
          background: "rgba(255,255,255,.92)",
          boxShadow: "0 16px 40px rgba(37, 99, 235, 0.06)"
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 14, marginBottom: 18 }}
        >
          <Box>
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#0f172a"
              }}
            >
              Desempenho comercial
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: "#64748b",
                marginTop: 3
              }}
            >
              Principais resultados de vendas no período.
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            style={{
              padding: 4,
              borderRadius: 12,
              background: "#f1f5f9",
              border: "1px solid #e2e8f0"
            }}
          >
            {periodOptions.map(option => (
              <Box
                key={option.value}
                onClick={() => setPeriod(option.value)}
                style={{
                  padding: "8px 13px",
                  borderRadius: 9,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 700,
                  color: period === option.value ? "#ffffff" : "#64748b",
                  background:
                    period === option.value
                      ? "linear-gradient(135deg, #2563eb, #3b82f6)"
                      : "transparent",
                  boxShadow:
                    period === option.value
                      ? "0 5px 14px rgba(37, 99, 235, .25)"
                      : "none",
                  transition: "all .2s ease"
                }}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Grid container spacing={2}>
          {commercialCards.map(card => (
            <Grid item xs={12} sm={6} lg={3} key={card.title}>
              <Box
                style={{
                  height: "100%",
                  minHeight: 132,
                  padding: 18,
                  borderRadius: 15,
                  boxSizing: "border-box",
                  border: `1px solid ${card.accent}20`,
                  borderTop: `3px solid ${card.accent}`,
                  background: card.background
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    color: "#64748b",
                    fontWeight: 700
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  variant="h5"
                  style={{
                    color: "#0f172a",
                    fontWeight: 800,
                    marginTop: 8
                  }}
                >
                  {loading && !data ? "..." : card.value}
                </Typography>

                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    color: "#94a3b8",
                    marginTop: 7
                  }}
                >
                  {card.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Grid container spacing={2} style={{ marginTop: 2 }}>
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            style={{
              height: "100%",
              borderRadius: 16,
              padding: 22,
              boxSizing: "border-box",
              border: "1px solid #e7edf4",
              background: "#ffffff"
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 4
              }}
            >
              Resumo de hoje
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: "#94a3b8",
                marginBottom: 20
              }}
            >
              Principais movimentações da operação.
            </Typography>

            <Grid container spacing={2}>
              {dailyMetrics.map(metric => (
                <Grid item xs={6} sm={3} key={metric.label}>
                  <Box
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      background: "#f8fafc",
                      border: "1px solid #edf2f7"
                    }}
                  >
                    <Typography
                      variant="h5"
                      style={{
                        color: "#0f172a",
                        fontWeight: 800
                      }}
                    >
                      {formatNumber(metric.value)}
                    </Typography>

                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        color: "#64748b",
                        marginTop: 5
                      }}
                    >
                      {metric.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            style={{
              height: "100%",
              borderRadius: 16,
              padding: 22,
              boxSizing: "border-box",
              border: "1px solid #e7edf4",
              background: "#ffffff"
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 4
              }}
            >
              Atenção necessária
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: "#94a3b8",
                marginBottom: 18
              }}
            >
              Situações que merecem acompanhamento.
            </Typography>

            {alerts.length === 0 ? (
              <Box
                style={{
                  padding: 18,
                  borderRadius: 14,
                  background: "#f0fdf4",
                  border: "1px solid #dcfce7"
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    color: "#15803d",
                    fontWeight: 600
                  }}
                >
                  Nenhuma situação crítica identificada agora.
                </Typography>
              </Box>
            ) : (
              alerts.map(alert => (
                <Box
                  key={alert.title}
                  display="flex"
                  alignItems="flex-start"
                  style={{
                    padding: 14,
                    marginBottom: 10,
                    borderRadius: 13,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa"
                  }}
                >
                  <Warning
                    style={{
                      color: "#f97316",
                      fontSize: 21,
                      marginRight: 11,
                      marginTop: 1
                    }}
                  />

                  <Box>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#7c2d12",
                        fontWeight: 700
                      }}
                    >
                      {alert.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        color: "#9a3412",
                        marginTop: 3
                      }}
                    >
                      {alert.description}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 2 }}>
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            style={{
              borderRadius: 16,
              padding: 22,
              border: "1px solid #e7edf4",
              background: "#ffffff"
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 4
              }}
            >
              Agenda de hoje
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: "#94a3b8",
                marginBottom: 18
              }}
            >
              Próximos compromissos da operação.
            </Typography>

            {agendaToday.length === 0 ? (
              <Typography
                variant="body2"
                style={{
                  color: "#94a3b8"
                }}
              >
                Nenhum agendamento para hoje.
              </Typography>
            ) : (
              agendaToday.map(item => (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  style={{
                    padding: "13px 0",
                    borderBottom: "1px solid #edf2f7"
                  }}
                >
                  <AccessTime
                    style={{
                      color: "#8b5cf6",
                      fontSize: 21,
                      marginRight: 12
                    }}
                  />

                  <Box style={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 700,
                        color: "#0f172a"
                      }}
                    >
                      {item.contactName}
                    </Typography>

                    <Typography
                      variant="caption"
                      style={{
                        color: "#64748b"
                      }}
                    >
                      {item.serviceName}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    style={{
                      color: "#475569",
                      fontWeight: 700
                    }}
                  >
                    {item.startTime
                      ? new Date(item.startTime).toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit"
                        })
                      : "--:--"}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            style={{
              borderRadius: 16,
              padding: 22,
              border: "1px solid #e7edf4",
              background: "#ffffff"
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: 4
              }}
            >
              Aguardando equipe
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: "#94a3b8",
                marginBottom: 18
              }}
            >
              Conversas que precisam de acompanhamento humano.
            </Typography>

            {aiPendingTickets.length === 0 ? (
              <Typography
                variant="body2"
                style={{
                  color: "#94a3b8"
                }}
              >
                Nenhuma conversa aguardando a equipe.
              </Typography>
            ) : (
              aiPendingTickets.slice(0, 6).map(ticket => (
                <Box
                  key={ticket.id}
                  display="flex"
                  alignItems="center"
                  onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                  style={{
                    padding: "13px 10px",
                    margin: "0 -10px",
                    borderBottom: "1px solid #edf2f7",
                    borderRadius: 10,
                    cursor: "pointer",
                    transition: "background 0.2s ease"
                  }}
                  onMouseEnter={event => {
                    event.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={event => {
                    event.currentTarget.style.background = "transparent";
                  }}
                >
                  <HeadsetMic
                    style={{
                      color: "#ef4444",
                      fontSize: 21,
                      marginRight: 12
                    }}
                  />

                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#0f172a",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {ticket.contactName}
                    </Typography>

                    <Typography
                      variant="caption"
                      style={{
                        color: "#94a3b8"
                      }}
                    >
                      {formatWaitingTime(ticket.updatedAt)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    style={{
                      color: "#2563eb",
                      fontWeight: 700,
                      marginLeft: 12
                    }}
                  >
                    Abrir
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
